/**
 * NewCustomerSignupModalOTP - OTP-enabled version of the signup modal
 *
 * This component implements the passwordless OTP authentication flow for the
 * new customer signup modal. It handles:
 * - Phone/email verification via OTP
 * - Returning user detection
 * - Pending user resume flow
 * - Email conflict resolution
 * - New user signup with Stripe payment
 *
 * Use this when PASSWORDLESS_OTP_ENABLED feature flag is true.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { authSession } from '@/auth/utils/authSession';
import Dialog from '@/components/Dialog';
import { fetchViewer } from '@/core/viewer';

import { ScreenRouter } from './components';
import { FLOW_CONFIG } from './constants';
import { useEmailLogin, useOTPFlow, useSignupFlow } from './hooks';
import { useStyles } from './styles';
import type { NewCustomerSignupModalProps } from './types';
import { mapBillingAccount } from './utils/mapBillingAccount';
import { hasCardOnFile } from './utils/paymentProgress';
import { type ScreenName, trackSMBModalClosed, trackSMBModalOpened } from './utils/smbAnalytics';

function NewCustomerSignupModalOTP({
  open,
  onClose,
  onComplete,
  expert,
  landingUrl,
  initialScreen,
  initialSchedulingData,
  authenticatedViewer,
}: NewCustomerSignupModalProps): JSX.Element {
  const s = useStyles();
  const navigate = useNavigate();

  // OTP flow hook - pass landingUrl prop for referral tracking
  const otpFlow = useOTPFlow({ onComplete, landingUrl });

  // Signup flow hook (for scheduling and user details form)
  const signupFlow = useSignupFlow({ expert, screen: otpFlow.screen, onComplete });

  // Email login hook
  const emailLogin = useEmailLogin({
    onSuccess: ({ user, profileState, stripeState, needsPaymentSetup }) => {
      // Set user data
      otpFlow.setUserData(user);

      // Set profile state if incomplete
      if (profileState) {
        otpFlow.setProfileState(profileState);
      }

      // Set stripe state if payment setup is needed
      if (stripeState) {
        otpFlow.setStripeState(stripeState);
      }

      // Route based on profile/billing status
      if (profileState && profileState.isPartial) {
        // Profile incomplete - go to details form
        otpFlow.goToScreen('details');
      } else if (needsPaymentSetup) {
        // No billing account - go to payment setup
        otpFlow.goToScreen('payment-new');
      } else {
        // Complete profile with billing - go to payment confirmation
        otpFlow.goToScreen('payment-existing');
      }
    },
    onError: (error) => {
      // Not console.error: Sentry captures those, and useEmailLogin has already
      // reported the underlying error (ENG-5831).
      console.warn('Login failed:', error);
    },
  });

  // Authenticated user state (when viewer prop is provided)
  const [isAuthenticatedUserMode, setIsAuthenticatedUserMode] = useState(false);

  // Expert client rate in cents with take rate applied. Null when the API could not
  // price the expert — kept null rather than substituting billRate (the pre-take-rate
  // figure, which understates the charge and discloses the expert's take-home) or 0
  // (which renders "$0"). This value fans out to three price displays (ENG-5571).
  const hourlyRate = expert.clientRate ?? null;

  // Track modal open time for analytics
  const modalOpenTime = useRef<Date | null>(null);
  // Ref to track latest screen for unmount cleanup (avoids stale closure in cleanup effect)
  const lastScreenRef = useRef(otpFlow.screen);
  // Guard to prevent duplicate modal opened events on re-renders
  const hasTrackedOpen = useRef(false);

  // Keep screen ref in sync
  useEffect(() => {
    lastScreenRef.current = otpFlow.screen;
  }, [otpFlow.screen]);

  // Track modal opened/closed lifecycle as a single effect
  useEffect(() => {
    if (open && expert && !hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      modalOpenTime.current = new Date();
      // Omitted rather than reported as 0 when unknown, so a fabricated rate does not
      // enter the funnel data.
      trackSMBModalOpened(expert.id, expert.name, hourlyRate ?? undefined);
    }

    // Cleanup: only fires on actual unmount (not screen transitions)
    return () => {
      if (modalOpenTime.current && hasTrackedOpen.current) {
        const screenMap: Record<string, string> = {
          booking: 'scheduling',
          phone: 'phone',
          verify: 'otp',
          details: 'details',
          'payment-new': 'payment',
          'payment-existing': 'payment',
          'email-conflict': 'details',
          'account-linked': 'details',
          'login-email': 'phone',
          confirmation: 'payment',
        };
        const lastScreen = (screenMap[lastScreenRef.current] || 'scheduling') as ScreenName;
        trackSMBModalClosed(lastScreen, modalOpenTime.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize state from props (for restoring state after email/password login redirect)
  React.useEffect(() => {
    if (initialScreen === 'payment-existing' && initialSchedulingData) {
      // Restore scheduling data
      signupFlow.handleSchedulingContinue({
        duration: initialSchedulingData.duration,
        dates: initialSchedulingData.dates,
        description: initialSchedulingData.description,
      });

      // Fetch billing account and set up returning user state using fetchViewer
      const token = authSession.getToken();
      if (token) {
        const client = authSession.createClient(token);
        fetchViewer(client.client)
          .then((viewer) => {
            const group = viewer.groups?.[0];
            const billingAccountData = group?.billing_account;

            if (billingAccountData) {
              // Set consolidated user data
              otpFlow.setUserData({
                id: viewer.id,
                type: 'existing',
                name:
                  viewer.profile?.name ||
                  `${viewer.profile?.first_name || ''} ${viewer.profile?.last_name || ''}`.trim() ||
                  'User',
                company: group?.name || '',
                maskedAddress: viewer.email?.address || '',
                billing: mapBillingAccount(billingAccountData),
                groupId: group?.id,
              });
              otpFlow.goToScreen('payment-existing');
            } else {
              // User has no billing account - go to payment setup (Flow 6)
              otpFlow.setUserData({
                id: viewer.id,
                type: 'existing',
                name:
                  viewer.profile?.name ||
                  `${viewer.profile?.first_name || ''} ${viewer.profile?.last_name || ''}`.trim() ||
                  'User',
                company: group?.name || '',
                maskedAddress: viewer.email?.address || '',
                billing: null,
                groupId: group?.id,
              });
              otpFlow.goToScreen('phone'); // Start from phone entry for payment setup
            }
          })
          .catch(() => {
            // Failed to fetch viewer - close modal
            console.error('Failed to fetch viewer for returning customer');
            onClose();
          });
      } else {
        // No token - close modal
        console.error('No authentication token for returning customer');
        onClose();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScreen, initialSchedulingData]);

  // Initialize state for authenticated user mode (when authenticatedViewer prop is provided)
  React.useEffect(() => {
    if (authenticatedViewer?.id) {
      // Set consolidated user data for authenticated user
      otpFlow.setUserData({
        id: authenticatedViewer.id,
        type: 'existing',
        name: authenticatedViewer.profileName || 'User',
        company: authenticatedViewer.groupName || '',
        maskedAddress: '',
        billing: authenticatedViewer.hasBillingAccount ? { paymentType: 'postpaid' } : null,
        groupId: authenticatedViewer.groupId,
      });

      setIsAuthenticatedUserMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedViewer]);

  // Handle continue from confirmation screen - navigate to consultations page
  const handleContinue = React.useCallback(() => {
    navigate('/consultations');
  }, [navigate]);

  // Handle continue from scheduling to next step
  // If already authenticated, go directly to payment confirmation
  // Otherwise, go to phone entry for authentication
  const handleSchedulingContinue = async (formValues?: {
    duration?: string;
    dates?: string[];
    description?: string;
  }) => {
    signupFlow.handleSchedulingContinue(formValues);

    // ENG-5549: once Stripe has confirmed this signup's card (or the signup went through),
    // go straight back to the payment step's retry. The details step would mint another
    // SetupIntent, and after a signup that went through the user already has billing.
    if (hasCardOnFile(signupFlow.paymentProgress, otpFlow.clientSecret)) {
      otpFlow.goToScreen('payment-new');
      return;
    }

    // For authenticated users (authenticatedViewer prop provided)
    if (isAuthenticatedUserMode && authenticatedViewer?.id) {
      if (authenticatedViewer.hasBillingAccount) {
        // User has billing - go to existing payment confirmation
        otpFlow.goToScreen('payment-existing');
      } else {
        // User needs billing setup - go to company name step first
        // Set up for partial profile mode with only company field
        otpFlow.setProfileState({
          isPartial: true,
          missingFields: ['company'],
          existingData: {
            firstName: '',
            lastName: '',
            email: '',
            company: authenticatedViewer.groupName || '',
          },
        });
        otpFlow.goToScreen('details');
      }
      return;
    }

    // For email login users with incomplete profile, route back to details
    if (otpFlow.isExistingUserFromEmailLogin && otpFlow.profileState?.isPartial) {
      otpFlow.goToScreen('details');
      return;
    }

    // For unauthenticated users (or email login users with complete profile)
    if (otpFlow.clientSecret) {
      // User already created profile and SetupIntent in this session - go to payment
      otpFlow.goToScreen('payment-new');
    } else if (otpFlow.isReturningUser) {
      otpFlow.goToScreen('payment-existing');
    } else {
      otpFlow.goToScreen('phone');
    }
  };

  return (
    <Dialog
      classes={{ paper: s.paper }}
      open={open}
      onClose={onClose}
      width={FLOW_CONFIG.MODAL_WIDTH}
    >
      <ScreenRouter
        screen={otpFlow.screen}
        expert={expert}
        hourlyRate={hourlyRate}
        landingUrl={landingUrl}
        authenticatedViewer={authenticatedViewer as any}
        otpFlow={otpFlow}
        signupFlow={signupFlow}
        emailLogin={emailLogin}
        isAuthenticatedUserMode={isAuthenticatedUserMode}
        modalOpenTime={modalOpenTime.current || undefined}
        onClose={onClose}
        onComplete={onComplete}
        onContinue={handleContinue}
        onSchedulingContinue={handleSchedulingContinue as any}
      />
    </Dialog>
  );
}

export default NewCustomerSignupModalOTP;
